import {Assert, CE} from "js-vextensions";
import {AssertNotify, RunInAction} from "web-vcore";
import {observable} from "mobx";
import {Float16ArrayFromBase64} from "../General/Numbers.js";
import {StripUndefined} from "../General/General.js";

export class GDBelt_RawDataChunk {
	constructor(data: Partial<GDBelt_RawDataChunk>) { Object.assign(this, data); }
	sessionIdPlusStartTime: string; // used as the store's key-path
	sessionId: string;
	startTime: number;
	/**
	 * The raw breath-force samples, as a plain (f64-grade) JS number array. This is the shape every
	 * in-memory consumer (BreathProcessor, BeltGraph, DetailChartAnnotations, SessionDataProcessor)
	 * reads from -- it abstracts over the actual persisted/transmitted format.
	 *
	 * For chunks written to disk before the f16-storage migration, this is the field persisted in
	 * IndexedDB (as a `number[]`). For chunks written after the migration, `samples_force_f16` is
	 * what is persisted (as a `Float16Array`); `NormalizeChunkToInMemoryShape` populates *this* field
	 * (widen-child) from that f16 buffer at load time, so callers see one consistent shape.
	 *
	 * NOTE: For sessions stored before the f16-storage migration, transfer over the wire will reduce
	 * the precision of these samples down to f16 levels (see `samples_force_f16_base64`).
	 */
	samples_force: number[] = []; // the in-memory normalized form; populated by NormalizeChunkToInMemoryShape on load
	/** Persisted in IndexedDB for chunks written after the f16-storage migration (a `Float16Array`). Older chunks lack this field; see `samples_force` for the legacy path. */
	samples_force_f16: Float16Array|n;
	/** Transient wire-shape: base64 of the raw little-endian f16 bytes. Populated by RequestSessionBreathData's sender (and immediately decoded+cleared by the receiver) when transferring over the remoteUserProcessesBridge. */
	samples_force_f16_base64: string|n;
}

/**
 * Given a chunk in any of the three possible formats -- (a) legacy `samples_force: number[]`
 * persisted in IndexedDB before the f16-storage migration; (b) new `samples_force_f16: Float16Array`
 * persisted after the migration; (c) wire-transient `samples_force_f16_base64: string` received over
 * the bridge -- returns a normalized in-memory chunk whose `samples_force: number[]` is populated
 * and whose f16/base64 storage fields are cleared.
 *
 * This is the single point of format-awareness used by both `GetChunksForSession`/`GetFirstChunkForSession`
 * (after reading from IndexedDB) and the bridge receive path (after decoding the wire DTO).
 */
export function NormalizeChunkToInMemoryShape(raw: GDBelt_RawDataChunk): GDBelt_RawDataChunk {
	// Resolve samples_force from whichever storage/wire field is populated, quantizing to f16-grade in
	// every case so the in-memory shape is consistent regardless of source format. (Re-quantizing an
	// already-f16-grade value is a no-op -- f16 -> f64 -> f16 is identity per the IEEE 754 subset lattice.)
	let samples_force: number[];
	if (raw.samples_force_f16_base64 != null) {
		// Wire-transient shape: decode base64 -> Float16Array -> widen to number[].
		samples_force = Array.from(Float16ArrayFromBase64(raw.samples_force_f16_base64));
	} else if (raw.samples_force_f16 != null) {
		// New persisted shape: widen Float16Array to number[] (the extra mantissa bits are zero -- no precision change).
		samples_force = Array.from(raw.samples_force_f16);
	} else if (raw.samples_force != null) {
		// legacy "read as-is" (kept for reference; it allowed precision-divergence between local vs wire-remote)
		//samples_force = raw.samples_force;

		// Legacy persisted shape (pre-migration f64 `number[]`). Quantize to f16 here on read, so that
		// analysis of an old session is identical regardless of which device runs it -- otherwise, the
		// origin device (which stored the chunk) would see f64-grade values locally while any device
		// receiving the same data over the wire would see f16-quantized values (since the wire sends
		// f64 samples through Float16Array.from() -> base64). Quantizing locally avoids that ongoing divergence in
		// favor of a one-time shift where old sessions may be processed slightly differently than pre-code-change.
		// This is a no-op round-trip for values that were already f16-grade (f16 -> f64 -> f16 is identity).
		samples_force = Array.from(Float16Array.from(raw.samples_force));
	} else {
		// No samples available (e.g. legacy chunk was empty); use an empty array.
		samples_force = [];
	}
	return new GDBelt_RawDataChunk({
		sessionId: raw.sessionId,
		sessionIdPlusStartTime: raw.sessionIdPlusStartTime,
		startTime: raw.startTime,
		samples_force,
		// The normalized in-memory form never carries the f16/b64 storage fields -- drop them so the
		// rest of the codebase can safely assume only `samples_force` is populated.
		samples_force_f16: undefined,
		samples_force_f16_base64: undefined,
	});
}

export class GDBelt_RawDataDB {
	private onInitHandlers = [] as (()=>void)[];
	EnsureInitDone() {
		return new Promise<void>((resolve, reject)=>{
			if (this.db) resolve();
			else this.onInitHandlers.push(resolve);
		});
	}

	private db: IDBDatabase;
	async Init() {
		return new Promise<void>((resolve, reject)=>{
			const request = indexedDB.open("GDBelt_RawData", 1);
			request.onupgradeneeded = event=>{
				const db = (event.target as IDBOpenDBRequest).result;
				const store = db.createObjectStore("chunks", {keyPath: "sessionIdPlusStartTime"});
				store.createIndex("sessionId", "sessionId", {unique: false});
				store.createIndex("startTime", "startTime", {unique: false});
			};
			request.onerror = ()=>reject(request.error);
			request.onsuccess = ()=>{
				this.db = request.result;
				this.onInitHandlers.forEach(handler=>handler());
				resolve();
			};
		});
	}

	// Store a single chunk for a session
	async StoreChunkForSession(chunk_partial: GDBelt_RawDataChunk) {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readwrite");
		const store = transaction.objectStore("chunks");

		AssertNotify(chunk_partial.sessionId != null && chunk_partial.startTime, "Chunk must have a sessionId and startTime.");
		const sessionIdPlusStartTime_calced = `${chunk_partial.sessionId}:${chunk_partial.startTime}`;
		AssertNotify(chunk_partial.sessionIdPlusStartTime == null || chunk_partial.sessionIdPlusStartTime == sessionIdPlusStartTime_calced, "The passed chunk.sessionIdPlusStartTime doesn't match `<id>:<startTime>`!");
		// Strip undefined-valued keys at the IndexedDB storage boundary so they don't bloat the on-disk
		// record (structured-clone writes both key-names and undefined values, which is wasteful). This
		// also lets in-memory chunk instances retain uniform V8 hidden-class shapes (by keeping all
		// declared fields populated, just as `undefined`), while on-disk records carry only the keys
		// that actually hold data.
		const chunk_final = StripUndefined({
			...chunk_partial,
			sessionIdPlusStartTime: `${chunk_partial.sessionId}:${chunk_partial.startTime}`,
		} as GDBelt_RawDataChunk);

		/*console.log(
			"Storing gd-belt raw-data-chunk:",
			chunk_final,
			//{...CE(chunk_final).ExcludeKeys("samples_force"), samples_force_length: chunk_final.samples_force.length},
		);*/

		return new Promise<void>((resolve, reject)=>{
			const request = store.put(chunk_final);
			request.onsuccess = ()=>resolve();
			request.onerror = ()=>reject(request.error);
		});
	}

	// this needs to be called when a session gets uploaded
	async CorrectSessionIDsForChunks(oldSessionID: string, newSessionID: string, deleteOld: boolean): Promise<GDBelt_RawDataChunk[]> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readwrite");
		const store = transaction.objectStore("chunks");
		const index = store.index("sessionId");

		return new Promise<GDBelt_RawDataChunk[]>((resolve, reject)=>{
			const request = index.openCursor(IDBKeyRange.only(oldSessionID));
			const oldEntries = [] as GDBelt_RawDataChunk[];
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					const oldChunk = cursor.value as GDBelt_RawDataChunk;

					// add new entry (with corrected sessionId)
					const newChunk = {
						...oldChunk,
						sessionId: newSessionID,
						sessionIdPlusStartTime: `${newSessionID}:${oldChunk.startTime}`,
					};
					const innerRequest = store.put(newChunk);
					innerRequest.onsuccess = ()=>{ /* do nothing */ };
					innerRequest.onerror = ()=>reject(innerRequest.error);

					// delete old entry
					if (deleteOld) {
						cursor.delete();
					}

					cursor.continue();
					oldEntries.push(oldChunk);
				} else {
					resolve(oldEntries);
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	async DeleteChunksForSession(sessionID: string): Promise<GDBelt_RawDataChunk[]> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readwrite");
		const store = transaction.objectStore("chunks");
		const index = store.index("sessionId");

		return new Promise<GDBelt_RawDataChunk[]>((resolve, reject)=>{
			const request = index.openCursor(IDBKeyRange.only(sessionID));
			const entriesDeleted = [] as GDBelt_RawDataChunk[];
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					const chunk = cursor.value as GDBelt_RawDataChunk;

					cursor.delete();

					cursor.continue();
					entriesDeleted.push(chunk);
				} else {
					resolve(entriesDeleted);
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	// Clear all stored data
	async ClearAllData(): Promise<void> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readwrite");
		const store = transaction.objectStore("chunks");

		return new Promise((resolve, reject)=>{
			const request = store.clear();
			request.onsuccess = ()=>resolve();
			request.onerror = ()=>reject(request.error);
		});
	}

	// Clear data for specific session
	async ClearSessionData(sessionId: string): Promise<void> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readwrite");
		const store = transaction.objectStore("chunks");
		const index = store.index("sessionId");

		return new Promise((resolve, reject)=>{
			const request = index.openCursor(IDBKeyRange.only(sessionId));
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					cursor.delete();
					cursor.continue();
				} else {
					resolve();
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}

	// chunk retrieval
	// ==========

	// call this after adding/deleting chunks in storage, to notify any mobx-listeners that the data has changed
	NotifyDataChangedForSession(sessionId: string) {
		RunInAction("GDBelt_RawDataDB.NotifyDataChangedForSession", ()=>{
			this.mobxData.sessionChunks_first_retrievalsInProgress.delete(sessionId);
			this.mobxData.sessionChunks_first.delete(sessionId);
			this.mobxData.sessionChunks_retrievalsInProgress.delete(sessionId);
			this.mobxData.sessionChunks.delete(sessionId);
		});
	}

	mobxData = {
		// these calls are lighter; they simply return the first chunk found for the session (so sessions-list can show whether breath-data exists)
		sessionChunks_first_retrievalsInProgress: new Map<string, boolean>(),
		sessionChunks_first: observable.map<string, GDBelt_RawDataChunk|null>(),

		// these calls are heavier; they retrieve all chunks for the session (so session details-chart can show all breath-data)
		sessionChunks_retrievalsInProgress: new Map<string, boolean>(),
		sessionChunks: observable.map<string, GDBelt_RawDataChunk[]>(),
	};

	// Get first chunk for a specific session
	async GetFirstChunkForSession(sessionId: string): Promise<GDBelt_RawDataChunk|null> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readonly");
		const store = transaction.objectStore("chunks");
		const index = store.index("sessionId");

		return new Promise((resolve, reject)=>{
			const request = index.openCursor(IDBKeyRange.only(sessionId));
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result as IDBCursorWithValue|null;
				// Normalize at the read boundary: abstract over whether the chunk was stored as samples_force (legacy) or samples_force_f16 (new).
				resolve(cursor ? NormalizeChunkToInMemoryShape(cursor.value as GDBelt_RawDataChunk) : null);
				return;
			};
			request.onerror = ()=>reject(request.error);
		});
	}
	GetFirstChunkForSession_MobX(sessionId: string): GDBelt_RawDataChunk|null {
		if (!this.mobxData.sessionChunks_first_retrievalsInProgress.get(sessionId) && !this.mobxData.sessionChunks_first.has(sessionId)) {
			this.mobxData.sessionChunks_first_retrievalsInProgress.set(sessionId, true);
			(async()=>{
				try {
					const chunk = await this.GetFirstChunkForSession(sessionId);
					console.log("Retrieved first-chunk for session:", sessionId, chunk);
					this.mobxData.sessionChunks_first_retrievalsInProgress.set(sessionId, false);
					RunInAction("GDBelt_RawDataDB.GetFirstChunkForSession_MobX", ()=>this.mobxData.sessionChunks_first.set(sessionId, chunk));
				} catch (err) {
					this.mobxData.sessionChunks_first_retrievalsInProgress.set(sessionId, false);
					console.error("Error retrieving first-chunk for session:", sessionId, err);
					//this.mobxData.sessionChunks_first.set(sessionId, null);
				}
			})();
		}
		console.log("Returning first-chunk for session:", sessionId, this.mobxData.sessionChunks_first.get(sessionId));
		return this.mobxData.sessionChunks_first.get(sessionId) ?? null;
	}

	// Get all chunks for a specific session
	async GetChunksForSession(sessionId: string): Promise<GDBelt_RawDataChunk[]> {
		await this.EnsureInitDone();
		const transaction = this.db.transaction(["chunks"], "readonly");
		const store = transaction.objectStore("chunks");
		const index = store.index("sessionId");

		return new Promise((resolve, reject)=>{
			const chunks = [] as GDBelt_RawDataChunk[];
			const request = index.openCursor(IDBKeyRange.only(sessionId));
			request.onsuccess = event=>{
				const cursor = (event.target as IDBRequest).result;
				if (cursor) {
					// Normalize at the read boundary: abstract over whether the chunk was stored as samples_force (legacy) or samples_force_f16 (new).
					chunks.push(NormalizeChunkToInMemoryShape(cursor.value as GDBelt_RawDataChunk));
					cursor.continue();
				} else {
					// Sort by startTime for chronological order
					chunks.sort((a, b)=>a.startTime - b.startTime);
					resolve(chunks);
				}
			};
			request.onerror = ()=>reject(request.error);
		});
	}
	GetChunksForSession_MobX(sessionId: string): GDBelt_RawDataChunk[] {
		if (!this.mobxData.sessionChunks_retrievalsInProgress.get(sessionId) && !this.mobxData.sessionChunks.get(sessionId)) {
			this.mobxData.sessionChunks_retrievalsInProgress.set(sessionId, true);
			(async()=>{
				try {
					const chunks = await this.GetChunksForSession(sessionId);
					console.log("Retrieved chunks for session:", sessionId, chunks);
					this.mobxData.sessionChunks_retrievalsInProgress.set(sessionId, false);
					RunInAction("GDBelt_RawDataDB.GetChunksForSession_MobX", ()=>this.mobxData.sessionChunks.set(sessionId, chunks));
				} catch (err) {
					this.mobxData.sessionChunks_retrievalsInProgress.set(sessionId, false);
					console.error("Error retrieving chunks for session:", sessionId, err);
					//this.mobxData.sessionChunks.set(sessionId, []);
				}
			})();
		}
		console.log("Returning chunks for session:", sessionId, this.mobxData.sessionChunks.get(sessionId) ?? []);
		return this.mobxData.sessionChunks.get(sessionId) ?? [];
	}
}

export const gdBelt_rawDataDB = new GDBelt_RawDataDB();
gdBelt_rawDataDB.Init(); // this is async, but that's fine; if a method is called prior to class init, it will just wait for initialization to complete before proceeding